import React from 'react';
import styled from 'styled-components';

/*
 * Bespoke, non-AG-Grid presentation for the Driver Leaderboard wallboard,
 * matching the handoff design (.context/design-handoff). Fully isolated — a
 * designer can restyle this file without affecting anything else. Values arrive
 * pre-formatted (formatting flows through the base view in useDriverLeaderboardCells);
 * everything here is presentation. Columns render in the configured order, each
 * styled by its display (rank badge / driver name / headline / standard).
 *
 * The whole table is ONE CSS grid so the header and every row share the exact
 * same column tracks (header labels stay aligned to their columns even with
 * content-sized columns). Header cells fill the first (auto) row; each data row
 * flows into a 1fr row so rows grow to fill the height. Sizes are design px —
 * the slide renders inside a 1920×1080 canvas scaled to fill the screen (see
 * ScalingCanvas).
 */

export interface LeaderboardDesignColumn {
  field: string;
  label: string;
  display: DriverLeaderboardFieldDisplay;
  isLeftAlign: boolean;
}

export interface LeaderboardDesignColumns {
  columns: LeaderboardDesignColumn[];
  hasRankChange: boolean;
}

export interface LeaderboardDesignRow {
  key: string;
  // The numeric rank from the rank column (drives medals + podium tint).
  // Undefined when no column renders as the rank.
  rankValue?: number;
  // number: positions moved (>0 up, <0 down, 0 unchanged); 'new': not on the
  // prior board; null: no compare range configured (no Rank Change column).
  rankChange: number | 'new' | null;
  cells: { field: string; value: string | number }[];
}

const MEDALS = [
  'var(--color-leaderboard-medal-gold)',
  'var(--color-leaderboard-medal-silver)',
  'var(--color-leaderboard-medal-bronze)',
];
const TINTS = [
  'var(--color-leaderboard-tint-gold)',
  'var(--color-leaderboard-tint-silver)',
  'var(--color-leaderboard-tint-bronze)',
];

// Horizontal breathing room between columns. Most columns are content-sized,
// so this padding is the only space between them; the driver column (1fr)
// gives up the difference. Padding rather than column-gap so the podium row
// tints stay continuous across the row.
const COL_PAD_X = 32;
const NAME_PAD_X = 16;
const START_PAD_X = 24;
const END_PAD_X = 40;
type Slot = DriverLeaderboardFieldDisplay | 'rankChange';
const cellPad = ({
  y,
  slot,
  isFirst,
  isLast,
}: {
  y: number;
  slot: Slot;
  isFirst: boolean;
  isLast: boolean;
}): string => {
  const x = slot === 'name' ? NAME_PAD_X : COL_PAD_X;
  return `${y}px ${isLast ? END_PAD_X : x}px ${y}px ${isFirst ? START_PAD_X : x}px`;
};

const isPodiumRank = (rank?: number): boolean =>
  rank !== undefined && rank >= 1 && rank <= 3;

// `$rowSlots` reserves a fixed number of equal-height rows (the page size) so a
// short last page keeps normal row height instead of stretching to fill.
const Grid = styled.div<{ $cols: string; $rowSlots: number }>`
  display: grid;
  grid-template-columns: ${(p) => p.$cols};
  grid-template-rows: ${(p) =>
    p.$rowSlots > 0 ? `auto repeat(${p.$rowSlots}, minmax(0, 1fr))` : 'auto'};
  grid-auto-rows: minmax(0, 1fr);
  flex: 1;
  min-height: 0;
  border: 1px solid var(--color-leaderboard-border);
  border-radius: 12px;
  overflow: hidden;
  font-family: 'Geist', ui-sans-serif, system-ui, sans-serif;
  color: var(--color-leaderboard-ink);
  background: var(--color-leaderboard-surface);
`;

const HeadCell = styled.div<{
  $right?: boolean;
  $hero?: boolean;
  $pad: string;
}>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$right ? 'flex-end' : 'flex-start')};
  padding: ${(p) => p.$pad};
  background: var(--color-leaderboard-header-bg);
  border-bottom: 1px solid var(--color-leaderboard-border);
  font-size: 26px;
  font-weight: 600;
  color: ${(p) =>
    p.$hero
      ? 'var(--color-leaderboard-ink)'
      : 'var(--color-leaderboard-header-label)'};
  white-space: nowrap;
`;

const DataCell = styled.div<{ $right?: boolean; $bg: string; $pad: string }>`
  display: flex;
  align-items: center;
  justify-content: ${(p) => (p.$right ? 'flex-end' : 'flex-start')};
  padding: ${(p) => p.$pad};
  background: ${(p) => p.$bg};
  border-top: 1px solid var(--color-leaderboard-row-border);
  min-width: 0;
`;

const RankBadge = styled.span<{ $bg: string; $fg: string }>`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  min-width: 52px;
  height: 48px;
  border-radius: 8px;
  background: ${(p) => p.$bg};
  color: ${(p) => p.$fg};
  font-family: 'Geist Mono', monospace;
  font-size: 32px;
  font-weight: 600;
`;

const NameText = styled.span`
  font-size: 38px;
  font-weight: 500;
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
`;

const SecondaryText = styled.span`
  font-size: 30px;
  font-weight: 500;
  font-variant-numeric: tabular-nums;
  color: var(--color-leaderboard-secondary);
  white-space: nowrap;
`;

const HeroText = styled.span`
  font-size: 38px;
  font-weight: 600;
  font-variant-numeric: tabular-nums;
  letter-spacing: -0.02em;
  white-space: nowrap;
`;

const ChangeInner = styled.span`
  font-size: 30px;
  font-weight: 600;
  white-space: nowrap;
  display: flex;
  align-items: center;
  gap: 8px;
`;

const Arrow = styled.span<{ $color: string }>`
  color: ${(p) => p.$color};
  font-size: 26px;
`;

const toDisplay = (value: string | number): string | number =>
  value === '' || value === null || value === undefined ? '-' : value;

const RankChangeContent = ({ change }: { change: number | 'new' | null }) => {
  if (change === null) {
    return null;
  }
  if (change === 'new') {
    return (
      <ChangeInner style={{ color: 'var(--color-leaderboard-new)' }}>
        New
      </ChangeInner>
    );
  }
  if (change === 0) {
    return (
      <ChangeInner style={{ color: 'var(--color-leaderboard-muted)' }}>
        —
      </ChangeInner>
    );
  }
  const isUp = change > 0;
  const magnitude = Math.abs(change);
  // ±1 is kept quiet (grey) per the design; larger moves use the state colours.
  const quiet = magnitude < 2;
  const numColor = quiet
    ? 'var(--color-leaderboard-muted)'
    : isUp
      ? 'var(--color-leaderboard-up)'
      : 'var(--color-leaderboard-down)';
  const arrowColor = quiet
    ? 'var(--color-leaderboard-quiet)'
    : isUp
      ? 'var(--color-leaderboard-up)'
      : 'var(--color-leaderboard-down-arrow)';
  return (
    <ChangeInner style={{ color: numColor }}>
      <Arrow $color={arrowColor}>{isUp ? '▲' : '▼'}</Arrow>
      <span>{magnitude}</span>
    </ChangeInner>
  );
};

const DriverLeaderboardTable = ({
  columns,
  rows,
  rowSlots,
}: {
  columns: LeaderboardDesignColumns;
  rows: LeaderboardDesignRow[];
  rowSlots: number;
}) => {
  // The Rank Change column sits straight after the rank column (or first when
  // no column renders as the rank).
  const rankIndex = columns.columns.findIndex((c) => c.display === 'rank');
  const slots: { slot: Slot; column?: LeaderboardDesignColumn }[] = [
    ...columns.columns.map((column) => ({
      slot: column.display as Slot,
      column,
    })),
  ];
  if (columns.hasRankChange) {
    slots.splice(rankIndex + 1, 0, { slot: 'rankChange' });
  }

  // The driver (name) column absorbs the remaining width. Without one, the
  // first left-aligned standard column does, so the table still fills.
  const flexIndex = (() => {
    const named = slots.findIndex((s) => s.slot === 'name');
    if (named >= 0) {
      return named;
    }
    const leftStandard = slots.findIndex(
      (s) => s.slot === 'standard' && s.column?.isLeftAlign,
    );
    if (leftStandard >= 0) {
      return leftStandard;
    }
    const nonRank = slots.findIndex(
      (s) => s.slot !== 'rank' && s.slot !== 'rankChange',
    );
    return nonRank >= 0 ? nonRank : 0;
  })();

  const gridCols = slots
    .map((s, i) => {
      if (s.slot === 'name' || i === flexIndex) {
        return 'minmax(0, 1fr)';
      }
      if (s.slot === 'rankChange') {
        return 'minmax(160px, max-content)';
      }
      return 'max-content';
    })
    .join(' ');

  const isRight = (s: { slot: Slot; column?: LeaderboardDesignColumn }) =>
    s.slot === 'headline' || (s.slot === 'standard' && !s.column?.isLeftAlign);
  const padFor = (y: number, i: number) =>
    cellPad({
      y,
      slot: slots[i].slot,
      isFirst: i === 0,
      isLast: i === slots.length - 1,
    });

  return (
    <Grid data-anim="table" $cols={gridCols} $rowSlots={rowSlots}>
      {/* Header row (auto height) */}
      {slots.map((s, i) => (
        <HeadCell
          key={s.column ? s.column.field : 'rankChange'}
          data-anim="thead"
          data-testid="leaderboard-header"
          $pad={padFor(18, i)}
          $right={isRight(s)}
          $hero={s.slot === 'headline'}
        >
          {s.column ? s.column.label : 'Rank Change'}
        </HeadCell>
      ))}

      {/* Data rows — each cell is a direct grid child so columns align exactly */}
      {rows.map((row, ri) => {
        const isPodium = isPodiumRank(row.rankValue);
        // Below the podium, every other row gets a faint stripe to help the eye
        // track across the wide row. Never on the first row, and never directly
        // against a podium tint.
        const isAfterPodium = ri > 0 && isPodiumRank(rows[ri - 1].rankValue);
        const isStriped = !isPodium && !isAfterPodium && ri > 0 && ri % 2 === 0;
        const bg = isPodium
          ? TINTS[(row.rankValue as number) - 1]
          : isStriped
            ? 'var(--color-leaderboard-row-stripe)'
            : 'var(--color-leaderboard-surface)';
        const valueByField = new Map(row.cells.map((c) => [c.field, c.value]));
        return (
          <React.Fragment key={row.key}>
            {slots.map((s, i) => {
              const value = s.column ? valueByField.get(s.column.field) : '';
              return (
                <DataCell
                  key={s.column ? s.column.field : 'rankChange'}
                  data-anim="row"
                  data-row={ri}
                  $bg={bg}
                  $pad={padFor(0, i)}
                  $right={isRight(s)}
                >
                  {s.slot === 'rankChange' && (
                    <RankChangeContent change={row.rankChange} />
                  )}
                  {s.slot === 'rank' && (
                    <RankBadge
                      $bg={
                        isPodium
                          ? MEDALS[(row.rankValue as number) - 1]
                          : 'transparent'
                      }
                      $fg={
                        isPodium
                          ? 'var(--color-leaderboard-surface)'
                          : 'var(--color-leaderboard-muted)'
                      }
                    >
                      {toDisplay(value ?? '')}
                    </RankBadge>
                  )}
                  {s.slot === 'name' && (
                    <NameText>{toDisplay(value ?? '')}</NameText>
                  )}
                  {s.slot === 'headline' && (
                    <HeroText>{toDisplay(value ?? '')}</HeroText>
                  )}
                  {s.slot === 'standard' && (
                    <SecondaryText>{toDisplay(value ?? '')}</SecondaryText>
                  )}
                </DataCell>
              );
            })}
          </React.Fragment>
        );
      })}
    </Grid>
  );
};

export default DriverLeaderboardTable;
